var kind                = require("enyo/kind"),
    Animated2DTransform = require("enyo-anim/Animated2DTransform");

/**
 * This component takes care of the complicated logic behind the positioning of
 * the page within the canvas viewport.
 *
 * The design goal for this component is to take care of scale and position,
 * alignment relative to the canvas viewport. In order to keep things working
 * smoothly, there are a few ground rules on how to interact with the state of
 * the transform:
 *
 * In order to animate the transform, targets must be applied using the
 * following properties:
 *  - [targetScaleX]{@link ott.image-reader.PageTransform#targetScaleX}
 *  - [targetScaleY]{@link ott.image-reader.PageTransform#targetScaleY}
 *  - [targetPositionX]{@link ott.image-reader.PageTransform#targetPositionX}
 *  - [targetPositionY]{@link ott.image-reader.PageTransform#targetPositionY}
 *
 * The transform origin is *not* meant to be animated. However, it is possible
 * to change the transform origin to correspond to coordinates on the canvas tag
 * using the following methods:
 *  - [applyOriginX]{@link ott.image-reader.PageTransform#applyOriginX}
 *  - [applyOriginY]{@link ott.image-reader.PageTransform#applyOriginY}
 *
 * Finally, a few helpers have been implemented to disallow and/or minimize
 * overflow or to center the page in the viewport. These alignment helpers can
 * be configures using the following properties:
 *  - [autoCenterX]{@link ott.image-reader.PageTransform#autoCenterX}
 *  - [autoCenterY]{@link ott.image-reader.PageTransform#autoCenterY}
 *  - [reduceOverflowLeft]{@link ott.image-reader.PageTransform#reduceOverflowLeft}
 *  - [reduceOverflowRight]{@link ott.image-reader.PageTransform#reduceOverflowRight}
 *  - [reduceOverflowTop]{@link ott.image-reader.PageTransform#reduceOverflowTop}
 *  - [reduceOverflowBottom]{@link ott.image-reader.PageTransform#reduceOverflowBottom}
 *
 * @class   ott.image-reader.PageTransform
 * @extends enyo-anim.Animated2DTransform
 * @public
 */
module.exports = kind(/** @lends  ott.image-reader.PageTransform.prototype */ {

    /**
     * @private
     */
    name: "ott.image-reader.PageTransform",

    /**
     * @private
     */
    kind: Animated2DTransform,

    /**
     * The width of the untransformed page in pixels
     *
     * @type {?number}
     * @public
     */
    width: null,

    /**
     * The height of the untransformed page in pixels
     *
     * @type {?number}
     * @public
     */
    height: null,

    /**
     * The viewport bounds
     *
     * @type {?enyo-canvas.Viewport}
     * @public
     */
    viewport: null,

    /**
     * The transition target for scaleX.
     *
     * @type {null|number|string}
     * @public
     */
    targetScaleX: null,

    /**
     * The transition target for scaleY.
     *
     * @type {null|number|string}
     * @public
     */
    targetScaleY: null,

    /**
     * The transition target for positionX.
     *
     * @type {null|number|string}
     * @public
     */
    targetPositionX: null,

    /**
     * The transition target for positionY.
     *
     * @type {null|number|string}
     * @public
     */
    targetPositionY: null,

    /**
     * The animated transform properties.
     *
     * @type {object}
     * @readonly
     * @public
     */
    animators: null,

    /**
     * Set the origin to the center of the page by default
     *
     * @private
     */
    importProps: kind.inherit(function(sup) {
        return function() {
            var animators;

            sup.apply(this, arguments);

            animators                      = this.animators;
            animators.originX.scalar.value = 0.5;
            animators.originY.scalar.value = 0.5;
        };
    }),

    /**
     * Check whether viewport bounds are fully defined.
     *
     * @return  {boolean}  `true` if defined, `false` otherwise.
     * @public
     */
    hasViewport: function() {
        var viewport = this.viewport;

        return Boolean(viewport
            && (viewport.x || viewport.x === 0)
            && (viewport.y || viewport.y === 0)
            && (viewport.width || viewport.width === 0)
            && (viewport.height || viewport.height === 0));
    },

    /**
     * Check whether the native dimensions of the transform are defined.
     *
     * @return  {boolean}  `true` if defined, `false` otherwise.
     * @public
     */
    hasDimensions: function() {
        var width  = this.width,
            height = this.height;

        return Boolean((width || width === 0)
            && (height || height === 0));
    },

    /**
     * Check whether all scalars of the transform are defined.
     *
     * @return  {boolean}  `true` if defined, `false` otherwise.
     * @public
     */
    hasTransform: function() {
        var animators = this.animators,
            scaleX    = animators.scaleX.scalar.value,
            scaleY    = animators.scaleY.scalar.value,
            originX   = animators.originX.scalar.value,
            originY   = animators.originY.scalar.value,
            positionX = animators.positionX.scalar.value,
            positionY = animators.positionY.scalar.value;

        return Boolean((scaleX || scaleX === 0)
            && (scaleY || scaleY === 0)
            && (originX || originX === 0)
            && (originY || originY === 0)
            && (positionX || positionX === 0)
            && (positionY || positionY === 0));
    },

    /**
     * Used internally to apply computed targets as soon as it is determined
     * that the transform has acquired enough parameters to be functional.
     *
     * @private
     */
    _isComputable: false,

    /**
     * Check whether enough parameters are present to compute a full transform.
     *
     * @return  {boolean}  `true` if transform can be computed, `false`
     *                     otherwise.
     * @public
     */
    canComputeTransform: function() {
        var animators     = this.animators,
            scaleX        = animators.scaleX.scalar.value,
            scaleY        = animators.scaleY.scalar.value,
            originX       = animators.originX.scalar.value,
            originY       = animators.originY.scalar.value,
            positionX     = animators.positionX.scalar.value,
            positionY     = animators.positionY.scalar.value,
            wasComputable = this._isComputable,
            isComputable;

        this._isComputable = isComputable = Boolean(this.hasViewport()
            && this.hasDimensions()
            && (scaleX || scaleX === 0 || this.targetScaleX !== null)
            && (scaleY || scaleY === 0 || this.targetScaleY !== null)
            && (originX || originX === 0)
            && (originY || originY === 0)
            && (positionX || positionX === 0 || this.targetPositionX !== null)
            && (positionY || positionY === 0 || this.targetPositionY !== null));

        // Apply computed targets once possible
        if (!wasComputable && isComputable) {
            this._applyTargetScaleX();
            this._applyTargetScaleY();
            this._applyTargetPositionX();
            this._applyTargetPositionY();
        }

        return isComputable;
    },

    /**
     * The transform is complete when viewport dimensions, page dimensions and
     * all scalars are set.
     *
     * @return  {boolean}  `true` if transformable, `false` otherwise.
     * @public
     */
    canTransform: function() {
        return this.hasViewport()
            && this.hasDimensions()
            && this.hasTransform();
    },

    // ...........................
    // Calculated Scales

    /**
     * The scale needed to fit this entire page inside the viewport, while never
     * zooming beyond 1:1 scale.
     *
     * @return {?number}  The min scale, or `null` if it cannot be calculated
     * @public
     */
    calcMinScale: function() {
        var viewport = this.viewport,
            width    = this.width,
            height   = this.height;

        if (!this.hasViewport() || !this.hasDimensions()) {
            return null;
        }

        return Math.min(1, Math.min(
            viewport.width / width,
            viewport.height / height
        ));
    },

    /**
     * The scale needed to display this page at least at 1:1 scale, or whatever
     * larger zoom factor is needed for the page to cover the entire viewport.
     *
     * @return {?number}  The max scale, or `null` if it cannot be calculated
     * @public
     */
    calcMaxScale: function() {
        var viewport = this.viewport,
            width    = this.width,
            height   = this.height;

        if (!this.hasViewport() || !this.hasDimensions()) {
            return null;
        }

        return Math.max(1, Math.max(
            viewport.width / width,
            viewport.height / height
        ));
    },

    /**
     * The scale at which the width of this page equals the width of the
     * viewport.
     *
     * @return {?number}  The scale, or `null` if it cannot be calculated
     * @public
     */
    calcContainScaleX: function() {
        var viewport = this.viewport,
            width    = this.width;

        if (!this.hasViewport() || !this.hasDimensions()) {
            return null;
        }

        return viewport.width / width;
    },

    /**
     * The at which the height of this page equals the height of the viewport.
     *
     * @return {?number}  The scale, or `null` if it cannot be calculated
     * @public
     */
    calcContainScaleY: function() {
        var viewport = this.viewport,
            height   = this.height;

        if (!this.hasViewport() || !this.hasDimensions()) {
            return null;
        }

        return viewport.height / height;
    },

    /**
     * The largest scale at which this page will not overflow the viewport.
     *
     * @return {?number}  The scale, or `null` if it cannot be calculated
     * @public
     */
    calcContainScale: function() {
        var viewport = this.viewport,
            width    = this.width,
            height   = this.height;

        if (!this.hasViewport() || !this.hasDimensions()) {
            return null;
        }

        return Math.min(
            viewport.width / width,
            viewport.height / height
        );
    },

    /**
     * The smallest scale at which this page will fill the entire viewport.
     *
     * @return {?number}  The scale, or `null` if it cannot be calculated
     * @public
     */
    calcCoverScale: function() {
        var viewport = this.viewport,
            width    = this.width,
            height   = this.height;

        if (!this.hasViewport() || !this.hasDimensions()) {
            return null;
        }

        return Math.max(
            viewport.width / width,
            viewport.height / height
        );
    },

    /**
     * Constrain scale to stay between [minScale]{@link
     * ott.image-reader.PageTransform#minScale} and [maxScale]{@link
     * ott.image-reader.PageTransform#maxScale}.
     *
     * @param  {Number}  scale  The scale value we want to constrain
     * @return {Number}  The constrained scale
     * @public
     */
    constrainScale: function(scale) {
        var minScale,
            maxScale;

        if (!this.hasViewport() || !this.hasDimensions()) {
            return null;
        }

        minScale = this.calcMinScale();
        maxScale = this.calcMaxScale();

        return Math.min(maxScale, Math.max(minScale, scale));
    },

    // ...........................
    // Calculated Dimensions

    /**
     * Calculates the width of the page according to the current transform.
     *
     * @return {?number}  The width, or `null` if it cannot be calculated
     * @public
     */
    calcWidth: function() {
        var width  = this.width,
            scaleX = this.animators.scaleX.scalar.value;

        if (!this.canTransform()) {
            return null;
        }

        return width * scaleX;
    },

    /**
     * Calculates the height of the page at the target scale.
     *
     * @return {?number}  The height, or `null` if it cannot be calculated
     * @public
     */
    calcHeight: function() {
        var height = this.height,
            scaleY = this.animators.scaleY.scalar.value;

        if (!this.canTransform()) {
            return null;
        }

        return height * scaleY;
    },

    // ...........................
    // Transform Origin

    /**
     * Calculates the new `originX` value, such that the transform origin on the
     * x-axis aligns with the provided x-coordinate relative to the top-left
     * corner of the viewport.
     *
     * @param  {number}  x  The x-coordinate in canvas space
     * @public
     */
    applyOriginX: function(x) {
        var viewport        = this.viewport,
            width           = this.width,
            animators       = this.animators,
            originX         = animators.originX.scalar,
            positionX       = animators.positionX.scalar,
            targetPositionX = animators.positionX.attractors.target,
            scaleX          = animators.scaleX.scalar.value,
            targetScaleX    = animators.scaleX.attractors.target.value,
            oldOriginX,
            newOriginX,
            positionDelta,
            targetPositionDelta;

        if (!this.canTransform()) {
            return;
        }

        // Convert x-coordinate to originX value
        oldOriginX = originX.value;
        newOriginX = (x + viewport.x - positionX.value + width * scaleX * oldOriginX) / (width * scaleX);

        // No change
        if (newOriginX === oldOriginX) {
            return;
        }

        // Set new originX
        originX.value = newOriginX;

        // Adjust the positionX coordinate to compensate for the new transform
        // origin.
        positionDelta   = (newOriginX - oldOriginX) * width * scaleX;
        positionX.value += positionDelta;

        // Adjust the positionX transition target to compensate for the new
        // transform origin.
        if (targetPositionX.value !== null) {
            if (targetScaleX !== null) {
                targetPositionDelta = (newOriginX - oldOriginX) * width * targetScaleX;
                this.set("targetPositionX", targetPositionX.value + targetPositionDelta);
            }
            else {
                this.set("targetPositionX", targetPositionX.value + positionDelta);
            }
        }
    },

    /**
     * Calculates the new `originY` value, such that the transform origin on the
     * y-axis aligns with the provided y-coordinate relative to the top-left
     * corner of the viewport.
     *
     * @param  {number}  y  The y-coordinate in canvas space
     * @public
     */
    applyOriginY: function(y) {
        var viewport        = this.viewport,
            height          = this.height,
            animators       = this.animators,
            scaleY          = animators.scaleY.scalar.value,
            targetScaleY    = animators.scaleY.attractors.target.value,
            originY         = animators.originY.scalar,
            positionY       = animators.positionY.scalar,
            targetPositionY = animators.positionY.attractors.target,
            oldOriginY,
            newOriginY,
            positionDelta,
            targetPositionDelta;

        if (!this.canTransform()) {
            return;
        }

        // Convert y-coordinate to originY value
        oldOriginY = originY.value;
        newOriginY = (y + viewport.y - positionY.value + (height * scaleY * oldOriginY)) / (height * scaleY);

        // No change
        if (newOriginY === oldOriginY) {
            return;
        }

        // Set new originY
        originY.value = newOriginY;

        // Adjust the positionY coordinate to compensate for the new transform
        // origin.
        positionDelta   = (newOriginY - oldOriginY) * height * scaleY;
        positionY.value += positionDelta;

        // Adjust the positionY transition target to compensate for the new
        // transform origin.
        if (targetPositionY.value !== null) {
            if (targetScaleY !== null) {
                targetPositionDelta = (newOriginY - oldOriginY) * height * targetScaleY;
                this.set("targetPositionY", targetPositionY.value + targetPositionDelta);
            }
            else {
                this.set("targetPositionY", targetPositionY.value + positionDelta);
            }
        }
    },

    // ...........................
    // Calculated Bounds

    /**
     * Calculates the `enyo-canvas.CanvasBounds` of the page according to the
     * current transform.
     *
     * @return {?enyo-canvas.CanvasBounds}  The bounds, or `null` if they cannot
     *                                      be calculated
     * @public
     */
    calcBounds: function() {
        var width     = this.width,
            height    = this.height,
            animators = this.animators,
            scaleX    = animators.scaleX.scalar.value,
            scaleY    = animators.scaleY.scalar.value,
            originX   = animators.originX.scalar.value,
            originY   = animators.originY.scalar.value,
            positionX = animators.positionX.scalar.value,
            positionY = animators.positionY.scalar.value,
            bounds    = {};

        if (!this.canTransform()) {
            return null;
        }

        bounds.x      = positionX - width * scaleX * originX;
        bounds.y      = positionY - height * scaleY * originY;
        bounds.width  = width * scaleX;
        bounds.height = height * scaleY;

        return bounds;
    },

    /**
     * Calculates bounds of the intersection between the viewport and the page
     * according to the current transform.
     *
     * If the bounds of the page do not intersect with the viewport this method
     * will return `null`.
     *
     * @return {?enyo-canvas.CanvasBounds}  The bounds, or `null` if they there
     *                                      is no intersection
     * @public
     */
    calcClippedBounds: function() {
        var viewport = this.viewport,
            bounds,
            x1,
            y1,
            x2,
            y2;

        if (!this.canTransform()) {
            return null;
        }

        bounds = this.calcBounds();

        // No intersection
        if (bounds.x >= viewport.x + viewport.width
            || bounds.x + bounds.width <= viewport.x
            || bounds.y >= viewport.y + viewport.height
            || bounds.y + bounds.height <= viewport.y
        ) {
            return null;
        }

        x1 = Math.max(bounds.x, viewport.x);
        y1 = Math.max(bounds.y, viewport.y);
        x2 = Math.min(bounds.x + bounds.width, viewport.x + viewport.width);
        y2 = Math.min(bounds.y + bounds.height, viewport.y + viewport.height);

        return {
            x:      x1,
            y:      y1,
            width:  x2 - x1,
            height: y2 - y1
        };
    },

    /**
     * Calculates whether the page is currently visible inside the viewport.
     *
     * @return {?boolean}  `true` if visible, `false` if not visible, `null` if
     *                     it cannot be calculated
     * @public
     */
    isVisible: function() {
        var bounds;

        if (!this.canTransform()) {
            return null;
        }

        bounds = this.calcClippedBounds();

        return bounds
            ? bounds.width > 0 && bounds.height > 0
            : false;
    },

    /**
     * Calculates the `enyo-canvas.CanvasBounds` of the page as it would be when
     * all current transitions are finished.
     *
     * @return {?enyo-canvas.CanvasBounds}  The bounds, or `null` if they cannot
     *                                      be calculated
     * @public
     */
    calcTargetBounds: function() {
        var width           = this.width,
            height          = this.height,
            animators       = this.animators,
            targetScaleX    = animators.scaleX.attractors.target.value,
            targetScaleY    = animators.scaleY.attractors.target.value,
            targetPositionX = animators.positionX.attractors.target.value,
            targetPositionY = animators.positionY.attractors.target.value,
            scaleX          = animators.scaleX.scalar.value,
            scaleY          = animators.scaleY.scalar.value,
            originX         = animators.originX.scalar.value,
            originY         = animators.originY.scalar.value,
            positionX       = animators.positionX.scalar.value,
            positionY       = animators.positionY.scalar.value,
            targetBounds    = {};

        if (!this.canComputeTransform()) {
            return null;
        }

        targetScaleX    = targetScaleX !== null ? targetScaleX : scaleX;
        targetScaleY    = targetScaleY !== null ? targetScaleY : scaleY;
        targetPositionX = targetPositionX !== null ? targetPositionX : positionX;
        targetPositionY = targetPositionY !== null ? targetPositionY : positionY;

        targetBounds.x      = targetPositionX - width * targetScaleX * originX;
        targetBounds.y      = targetPositionY - height * targetScaleY * originY;
        targetBounds.width  = width * targetScaleX;
        targetBounds.height = height * targetScaleY;

        return targetBounds;
    },

    /**
     * Calculates bounds of the intersection between the viewport and the page
     * as it would be when all current transitions are finished.
     *
     * If the bounds of the page do not intersect with the viewport this method
     * will return `null`.
     *
     * @return {?enyo-canvas.CanvasBounds}  The bounds, or `null` if they there
     *                                      is no intersection
     * @public
     */
    calcClippedTargetBounds: function() {
        var viewport = this.viewport,
            targetBounds,
            x1,
            y1,
            x2,
            y2;

        if (!this.canComputeTransform()) {
            return null;
        }

        targetBounds = this.calcTargetBounds();

        // No intersection
        if (targetBounds.x >= viewport.x + viewport.width
            || targetBounds.x + targetBounds.width <= viewport.x
            || targetBounds.y >= viewport.y + viewport.height
            || targetBounds.y + targetBounds.height <= viewport.y
        ) {
            return null;
        }

        x1 = Math.max(targetBounds.x, viewport.x);
        y1 = Math.max(targetBounds.y, viewport.y);
        x2 = Math.min(targetBounds.x + targetBounds.width, viewport.x + viewport.width);
        y2 = Math.min(targetBounds.y + targetBounds.height, viewport.y + viewport.height);

        return {
            x:      x1,
            y:      y1,
            width:  x2 - x1,
            height: y2 - y1
        };
    },

    /**
     * Calculates whether the page will be visible inside the viewport once all
     * current transitions are finished.
     *
     * @return {?boolean}  `true` if visible, `false` if not visible, `null` if
     *                     it cannot be calculated
     * @public
     */
    isTargetVisible: function() {
        var bounds;

        if (!this.canComputeTransform()) {
            return null;
        }

        bounds = this.calcClippedTargetBounds();

        return bounds
            ? bounds.width > 0 && bounds.height > 0
            : false;
    },

    // ...........................
    // Calculate Overflow

    /**
     * Calculates the amount by which the page breaks out of the viewport on the
     * left according to the current transform.
     *
     * @return {?number}  The amount of overflow, or `null` if it cannot be
     *                    calculated
     * @public
     */
    calcOverflowLeft: function() {
        var width     = this.width,
            viewport  = this.viewport,
            animators = this.animators,
            scaleX    = animators.scaleX.scalar.value,
            originX   = animators.originX.scalar.value,
            positionX = animators.positionX.scalar.value,
            xLeft;

        if (!this.canTransform()) {
            return null;
        }

        xLeft = positionX - width * scaleX * originX;

        return xLeft < viewport.x
            ? viewport.x - xLeft
            : 0;
    },

    /**
     * Calculates the amount by which the page breaks out of the viewport on the
     * right according to the current transform.
     *
     * @return {?number}  The amount of overflow, or `null` if it cannot be
     *                    calculated
     * @public
     */
    calcOverflowRight: function() {
        var width     = this.width,
            viewport  = this.viewport,
            animators = this.animators,
            scaleX    = animators.scaleX.scalar.value,
            originX   = animators.originX.scalar.value,
            positionX = animators.positionX.scalar.value,
            xRight;

        if (!this.canTransform()) {
            return null;
        }

        xRight = positionX + width * scaleX * (1 - originX);

        return xRight > viewport.x + viewport.width
            ? xRight - (viewport.x + viewport.width)
            : 0;
    },

    /**
     * Calculates the amount by which the page breaks out of the viewport at the
     * top according to the current transform.
     *
     * @return {?number}  The amount of overflow, or `null` if it cannot be
     *                    calculated
     * @public
     */
    calcOverflowTop: function() {
        var height    = this.height,
            viewport  = this.viewport,
            animators = this.animators,
            scaleY    = animators.scaleY.scalar.value,
            originY   = animators.originY.scalar.value,
            positionY = animators.positionY.scalar.value,
            yLeft;

        if (!this.canTransform()) {
            return null;
        }

        yLeft = positionY - height * scaleY * originY;

        return yLeft < viewport.y
            ? viewport.y - yLeft
            : 0;
    },

    /**
     * Calculates the amount by which the page breaks out of the viewport at the
     * bottom according to the current transform.
     *
     * @return {?number}  The amount of overflow, or `null` if it cannot be
     *                    calculated
     * @public
     */
    calcOverflowBottom: function() {
        var height    = this.height,
            viewport  = this.viewport,
            animators = this.animators,
            scaleY    = animators.scaleY.scalar.value,
            originY   = animators.originY.scalar.value,
            positionY = animators.positionY.scalar.value,
            yBottom;

        if (!this.canTransform()) {
            return null;
        }

        yBottom = positionY + height * scaleY * (1 - originY);

        return yBottom > viewport.y + viewport.height
            ? yBottom - (viewport.y + viewport.height)
            : 0;
    },

    /**
     * Calculates if the page is overflowing the viewport.
     *
     * @return {?boolean}  `true` if overflowing, `false` if not overflowing,
     *                     `null` if it cannot be calculated
     * @public
     */
    isOverflowing: function() {
        var viewport = this.viewport,
            bounds;

        if (!this.canTransform()) {
            return null;
        }

        bounds = this.calcBounds();

        return bounds.x < viewport.x
            || bounds.y < viewport.y
            || bounds.x + bounds.width > viewport.x + viewport.width
            || bounds.y + bounds.height > viewport.y + viewport.height;
    },

    // ...........................
    // Calculated Alignment Coordinates

    /**
     * The position at which the page is fully outside the viewport on the left,
     * given the supplied originX and scaleX parameters.
     *
     * @param  {?number}  scaleX   The `scaleX` value
     * @param  {?number}  originX  The `originX` value
     * @return {?number}  The position, or `null` if it cannot be calculated
     * @public
     */
    calcHideLeftPosition: function(scaleX, originX) {
        var viewport = this.viewport,
            width    = this.width;

        if (!this.hasViewport()
            || !this.hasDimensions()
            || scaleX === null
            || originX === null) {
            return null;
        }

        // Distance 1 extra pixel, to prevent issues with rounding errors
        return viewport.x - ((1 - originX) * width * scaleX) - 1;
    },

    /**
     * The position at which the page is fully outside the viewport on the
     * right, given the supplied originX and scaleX parameters.
     *
     * @param  {?number}  scaleX   The `scaleX` value
     * @param  {?number}  originX  The `originX` value
     * @return {?number}  The position, or `null` if it cannot be calculated
     * @public
     */
    calcHideRightPosition: function(scaleX, originX) {
        var viewport = this.viewport,
            width    = this.width;

        if (!this.hasViewport()
            || !this.hasDimensions()
            || scaleX === null
            || originX === null) {
            return null;
        }

        // Offset 1 extra pixel, to prevent issues with rounding errors
        return viewport.x + viewport.width + (originX * width * scaleX) + 1;
    },

    /**
     * The position at which the page is fully outside the viewport on the top,
     * given the supplied originY and scaleY parameters.
     *
     * @param  {?number}  scaleY   The `scaleY` value
     * @param  {?number}  originY  The `originY` value
     * @return {?number}  The position, or `null` if it cannot be calculated
     * @public
     */
    calcHideTopPosition: function(scaleY, originY) {
        var viewport = this.viewport,
            height   = this.height;

        if (!this.hasViewport()
            || !this.hasDimensions()
            || scaleY === null
            || originY === null) {
            return null;
        }

        // Offset 1 extra pixel, to prevent issues with rounding errors
        return viewport.y - ((1 - originY) * height * scaleY) - 1;
    },

    /**
     * The position at which the page is fully outside the viewport on the
     * bottom, given the supplied originY and scaleY parameters.
     *
     * @param  {?number}  scaleY   The `scaleY` value
     * @param  {?number}  originY  The `originY` value
     * @return {?number}  The position, or `null` if it cannot be calculated
     * @public
     */
    calcHideBottomPosition: function(scaleY, originY) {
        var viewport = this.viewport,
            height   = this.height;

        if (!this.hasViewport()
            || !this.hasDimensions()
            || scaleY === null
            || originY === null) {
            return null;
        }

        // Offset 1 extra pixel, to prevent issues with rounding errors
        return viewport.y + viewport.height + (originY * height * scaleY) + 1;
    },

    /**
     * The position at which the left edge of the page is positioned at the left
     * edge of the viewport, given the supplied originX and scaleX parameters.
     *
     * @param  {?number}  scaleX   The `scaleX` value
     * @param  {?number}  originX  The `originX` value
     * @return {?number}  The position, or `null` if it cannot be calculated
     * @public
     */
    calcAlignLeftPosition: function(scaleX, originX) {
        var viewport = this.viewport,
            width    = this.width;

        if (!this.hasViewport()
            || !this.hasDimensions()
            || scaleX === null
            || originX === null) {
            return null;
        }

        return viewport.x + (originX * width * scaleX);
    },

    /**
     * The position at which the right edge of the page is positioned at the
     * right edge of the viewport, given the supplied originX and scaleX
     * parameters.
     *
     * @param  {?number}  scaleX   The `scaleX` value
     * @param  {?number}  originX  The `originX` value
     * @return {?number}  The position, or `null` if it cannot be calculated
     * @public
     */
    calcAlignRightPosition: function(scaleX, originX) {
        var viewport = this.viewport,
            width    = this.width;

        if (!this.hasViewport()
            || !this.hasDimensions()
            || scaleX === null
            || originX === null) {
            return null;
        }

        return viewport.x + viewport.width - ((1 - originX) * width * scaleX);
    },

    /**
     * The position at which the top edge of the page is positioned at the top
     * edge of the viewport, given the supplied originY and scaleY parameters.
     *
     * @param  {?number}  scaleY   The `scaleY` value
     * @param  {?number}  originY  The `originY` value
     * @return {?number}  The position, or `null` if it cannot be calculated
     * @public
     */
    calcAlignTopPosition: function(scaleY, originY) {
        var viewport = this.viewport,
            height   = this.height;

        if (!this.hasViewport()
            || !this.hasDimensions()
            || scaleY === null
            || originY === null) {
            return null;
        }

        return viewport.y + (originY * height * scaleY);
    },

    /**
     * The position at which the bottom edge of the page is positioned at the
     * bottom edge of the viewport, given the supplied originY and scaleY
     * parameters.
     *
     * @param  {?number}  scaleY   The `scaleY` value
     * @param  {?number}  originY  The `originY` value
     * @return {?number}  The position, or `null` if it cannot be calculated
     * @public
     */
    calcAlignBottomPosition: function(scaleY, originY) {
        var viewport = this.viewport,
            height   = this.height;

        if (!this.hasViewport()
            || !this.hasDimensions()
            || scaleY === null
            || originY === null) {
            return null;
        }

        return viewport.y + viewport.height - ((1 - originY) * height * scaleY);
    },

    /**
     * The position at which the page is horizontally centered in the viewport,
     * given the supplied originX and scaleX parameters.
     *
     * @param  {?number}  scaleX   The `scaleX` value
     * @param  {?number}  originX  The `originX` value
     * @return {?number}  The position, or `null` if it cannot be calculated
     * @public
     */
    calcCenterXPosition: function(scaleX, originX) {
        var viewport = this.viewport,
            width    = this.width;

        if (!this.hasViewport()
            || !this.hasDimensions()
            || scaleX === null
            || originX === null) {
            return null;
        }

        return viewport.x + (viewport.width * 0.5) + ((-0.5 + originX) * width * scaleX);
    },

    /**
     * The position at which the page is vertically centered in the viewport,
     * given the supplied originY and scaleY parameters.
     *
     * @param  {?number}  scaleY   The `scaleY` value
     * @param  {?number}  originY  The `originY` value
     * @return {?number}  The position, or `null` if it cannot be calculated
     * @public
     */
    calcCenterYPosition: function(scaleY, originY) {
        var viewport = this.viewport,
            height   = this.height;

        if (!this.hasViewport()
            || !this.hasDimensions()
            || scaleY === null
            || originY === null) {
            return null;
        }

        return viewport.y + (viewport.height * 0.5) + ((-0.5 + originY) * height * scaleY);
    },

    // ...........................
    // Calculated Distance from Center

    /**
     * Calculates how many pixels the page is away from having it's left edge
     * aligned with the left edge of the viewport.
     *
     * @return {?number}  The distance from left alignment, or `null` if it
     *                    cannot be calculated.
     * @public
     */
    calcDistanceFromLeft: function() {
        var animators = this.animators,
            positionX = this.animators.positionX.scalar.value,
            alignedX;

        if (!this.canTransform()) {
            return null;
        }

        alignedX = this.calcAlignLeftPosition(
            animators.scaleX.scalar.value,
            animators.originX.scalar.value
        );

        return positionX - alignedX;
    },

    /**
     * Calculates how many pixels the page is away from having it's right edge
     * aligned with the right edge of the viewport.
     *
     * @return {?number}  The distance from right alignment, or `null` if it
     *                    cannot be calculated.
     * @public
     */
    calcDistanceFromRight: function() {
        var animators = this.animators,
            positionX = this.animators.positionX.scalar.value,
            alignedX;

        if (!this.canTransform()) {
            return null;
        }

        alignedX = this.calcAlignRightPosition(
            animators.scaleX.scalar.value,
            animators.originX.scalar.value
        );

        return positionX - alignedX;
    },

    /**
     * Calculates how many pixels the page is away from having it's top edge
     * aligned with the top edge of the viewport.
     *
     * @return {?number}  The distance from top alignment, or `null` if it
     *                    cannot be calculated.
     * @public
     */
    calcDistanceFromTop: function() {
        var animators = this.animators,
            positionY = this.animators.positionY.scalar.value,
            alignedY;

        if (!this.canTransform()) {
            return null;
        }

        alignedY = this.calcAlignTopPosition(
            animators.scaleY.scalar.value,
            animators.originY.scalar.value
        );

        return positionY - alignedY;
    },

    /**
     * Calculates how many pixels the page is away from having it's bottom edge
     * aligned with the bottom edge of the viewport.
     *
     * @return {?number}  The distance from bottom alignment, or `null` if it
     *                    cannot be calculated.
     * @public
     */
    calcDistanceFromBottom: function() {
        var animators = this.animators,
            positionY = this.animators.positionY.scalar.value,
            alignedY;

        if (!this.canTransform()) {
            return null;
        }

        alignedY = this.calcAlignBottomPosition(
            animators.scaleY.scalar.value,
            animators.originY.scalar.value
        );

        return positionY - alignedY;
    },

    /**
     * Calculates how many pixels the page is away from being centered
     * horizontally given the current transform.
     *
     * @return {?number}  The distance from center, or `null` if it cannot be
     *                    calculated.
     * @public
     */
    calcDistanceFromHorizontalCenter: function() {
        var animators = this.animators,
            positionX = this.animators.positionX.scalar.value,
            centerX;

        if (!this.canTransform()) {
            return null;
        }

        centerX = this.calcCenterXPosition(
            animators.scaleX.scalar.value,
            animators.originX.scalar.value
        );

        return positionX - centerX;
    },

    /**
     * Calculates how many pixels the page is away from being centered
     * vertically given the current transform.
     *
     * @return {?number}  The distance from center, or `null` if it cannot be
     *                    calculated.
     * @public
     */
    calcDistanceFromVerticalCenter: function() {
        var animators = this.animators,
            positionY = this.animators.positionY.scalar.value,
            centerY;

        if (!this.canTransform()) {
            return null;
        }

        centerY = this.calcCenterYPosition(
            animators.scaleY.scalar.value,
            animators.originY.scalar.value
        );

        return positionY - centerY;
    },

    // ...........................
    // Transition Targets

    /**
     * @private
     */
    targetScaleXChanged: function() {
        if (this.canComputeTransform()) {
            this._applyTargetScaleX();

            if (typeof this.targetPositionX === "string") {
                this._applyTargetPositionX();
            }
        }
    },

    /**
     * @private
     */
    targetScaleYChanged: function() {
        if (this.canComputeTransform()) {
            this._applyTargetScaleY();

            if (typeof this.targetPositionY === "string") {
                this._applyTargetPositionY();
            }
        }
    },

    /**
     * @private
     */
    targetPositionXChanged: function() {
        this.canComputeTransform() && this._applyTargetPositionX();
    },

    /**
     * @private
     */
    targetPositionYChanged: function() {
        this.canComputeTransform() && this._applyTargetPositionY();
    },

    /**
     * Calculate the scaleX transition target as needed and apply it.
     *
     * This method is not meant for external use and does *not* check if all
     * required parameters are defined.
     *
     * @private
     */
    _applyTargetScaleX: function() {
        var attractor = this.animators.scaleX.attractors.target,
            target    = this.targetScaleX;

        switch (target) {
            case "min":
                target = this.calcMinScale();
                break;
            case "containX":
                target = this.calcContainScaleX();
                break;
            case "containY":
                target = this.calcContainScaleY();
                break;
            case "contain":
                target = this.calcContainScale();
                break;
            case "cover":
                target = this.calcCoverScale();
                break;
            case "max":
                target = this.calcMaxScale();
                break;
            default:
                break;
        }

        if (typeof target === "number") {
            attractor.value = this.constrainScale(target);
        }
        else if (target === null) {
            attractor.clear();
        }
    },

    /**
     * Calculate the scaleY transition target as needed and apply it.
     *
     * This method is not meant for external use and does *not* check if all
     * required parameters are defined.
     *
     * @private
     */
    _applyTargetScaleY: function() {
        var attractor = this.animators.scaleY.attractors.target,
            target    = this.targetScaleY;

        switch (target) {
            case "min":
                target = this.calcMinScale();
                break;
            case "containX":
                target = this.calcContainScaleX();
                break;
            case "containY":
                target = this.calcContainScaleY();
                break;
            case "contain":
                target = this.calcContainScale();
                break;
            case "cover":
                target = this.calcCoverScale();
                break;
            case "max":
                target = this.calcMaxScale();
                break;
            default:
                break;
        }

        if (typeof target === "number") {
            attractor.value = this.constrainScale(target);
        }
        else if (target === null) {
            attractor.clear();
        }
    },

    /**
     * Calculate the positionX transition target as needed and apply it.
     *
     * This method is not meant for external use and does *not* check if all
     * required parameters are defined.
     *
     * @private
     */
    _applyTargetPositionX: function() {
        var animators     = this.animators,
            target        = this.targetPositionX,
            scaleAnimator = animators.scaleX,
            attractor     = animators.positionX.attractors.target,
            originX,
            scaleX;

        scaleX  = scaleAnimator.attractors.target.value;
        scaleX  = scaleX === null ? scaleAnimator.scalar.value : scaleX;
        originX = animators.originX.scalar.value;

        switch (target) {
            case "hideLeft":
                target = this.calcHideLeftPosition(scaleX, originX);
                break;
            case "alignLeft":
                target = this.calcAlignLeftPosition(scaleX, originX);
                break;
            case "center":
                target = this.calcCenterXPosition(scaleX, originX);
                break;
            case "alignRight":
                target = this.calcAlignRightPosition(scaleX, originX);
                break;
            case "hideRight":
                target = this.calcHideRightPosition(scaleX, originX);
                break;
            default:
                break;
        }

        if (typeof target === "number") {
            attractor.value = target;
        }
        else if (target === null) {
            attractor.clear();
        }
    },

    /**
     * Calculate the positionY transition target as needed and apply it.
     *
     * This method is not meant for external use and does *not* check if all
     * required parameters are defined.
     *
     * @private
     */
    _applyTargetPositionY: function() {
        var animators     = this.animators,
            target        = this.targetPositionY,
            scaleAnimator = animators.scaleY,
            attractor     = animators.positionY.attractors.target,
            originY,
            scaleY;

        originY = animators.originY.scalar.value;
        scaleY  = scaleAnimator.attractors.target.value;
        scaleY  = scaleY === null ? scaleAnimator.scalar.value : scaleY;

        switch (target) {
            case "hideTop":
                target = this.calcHideTopPosition(scaleY, originY);
                break;
            case "alignTop":
                target = this.calcAlignTopPosition(scaleY, originY);
                break;
            case "center":
                target = this.calcCenterYPosition(scaleY, originY);
                break;
            case "alignBottom":
                target = this.calcAlignBottomPosition(scaleY, originY);
                break;
            case "hideBottom":
                target = this.calcHideBottomPosition(scaleY, originY);
                break;
            default:
                break;
        }

        if (typeof target === "number") {
            attractor.value = target;
        }
        else if (target === null) {
            attractor.clear();
        }
    },

    // ...........................
    // Automatic Alignment

    /**
     * Configure how to automatically center the page horizontally within the
     * viewport.
     *
     *  - `"guide"` Only allows moving closer to centered position
     *  - `"snap"` Instantly snaps to a horizontally centered position
     *  - `"animate"` Sets a new animation target to horizontally centered
     *    position
     *  - `false` No automatic centering
     *
     * @type    {string|boolean}
     * @default false
     * @public
     */
    autoCenterX: false,

    /**
     * Configure how to automatically center the page vertically within the
     * viewport.
     *
     *  - `"guide"` Only allows moving closer to centered position
     *  - `"snap"` Instantly snaps to a vertically centered position
     *  - `"animate"` Sets a new animation target to vertically centered
     *    position
     *  - `false` No automatic centering
     *
     * @type    {string|boolean}
     * @default false
     * @public
     */
    autoCenterY: false,

    /**
     * Configure how to deal with the page overflowing at the left edge of the
     * viewport.
     *
     *  - `"guide"` Helps prevent the page from overflowing the viewport on the
     *    left any further than is necessary to reveal the right side of the
     *    page inside of the viewport.
     *  - `"snap"` Prevents overflowing any further than is necessary to reveal
     *    the right side of the page inside of the viewport by instantly
     *    snapping to the position of least overflow.
     *  - `"animate"` Prevents overflowing any further than is necessary to
     *    reveal the right side of the page inside of the viewport by setting a
     *    new animation target to the position of least overflow.
     *  - `false` Allow overflow
     *
     * @type {string|boolean}
     * @default false
     * @public
     */
    reduceOverflowLeft: false,

    /**
     * Configure how to deal with the page overflowing at the right edge of the
     * viewport.
     *
     *  - `"guide"` Helps prevent the page from overflowing the viewport on the
     *    right any further than is necessary to reveal the left side of the
     *    page inside of the viewport.
     *  - `"snap"` Prevents overflowing any further than is necessary to reveal
     *    the left side of the page inside of the viewport by instantly
     *    snapping to the position of least overflow.
     *  - `"animate"` Prevents overflowing any further than is necessary to
     *    reveal the left side of the page inside of the viewport by setting a
     *    new animation target to the position of least overflow.
     *  - `false` Allow overflow
     *
     * @type {string|boolean}
     * @default false
     * @public
     */
    reduceOverflowRight: false,

    /**
     * Configure how to deal with the page overflowing at the top edge of the
     * viewport.
     *
     *  - `"guide"` Helps prevent the page from overflowing the viewport on the
     *    top any further than is necessary to reveal the bottom of the
     *    page inside of the viewport.
     *  - `"snap"` Prevents overflowing any further than is necessary to reveal
     *    the bottom of the page inside of the viewport by instantly
     *    snapping to the position of least overflow.
     *  - `"animate"` Prevents overflowing any further than is necessary to
     *    reveal the bottom of the page inside of the viewport by setting a
     *    new animation target to the position of least overflow.
     *  - `false` Allow overflow
     *
     * @type {string|boolean}
     * @default false
     * @public
     */
    reduceOverflowTop: false,

    /**
     * Configure how to deal with the page overflowing at the bottom edge of the
     * viewport.
     *
     *  - `"guide"` Helps prevent the page from overflowing the viewport on the
     *    bottom any further than is necessary to reveal the top of the
     *    page inside of the viewport.
     *  - `"snap"` Prevents overflowing any further than is necessary to reveal
     *    the top of the page inside of the viewport by instantly
     *    snapping to the position of least overflow.
     *  - `"animate"` Prevents overflowing any further than is necessary to
     *    reveal the top of the page inside of the viewport by setting a
     *    new animation target to the position of least overflow.
     *  - `false` Allow overflow
     *
     * @type {string|boolean}
     * @default false
     * @public
     */
    reduceOverflowBottom: false,

    /**
     * Automatically align the page.
     *
     * This method is not meant for external use and does *not* check if all
     * required parameters are defined.
     *
     * @private
     */
    autoAlignment: function() {
        var animators            = this.animators,
            scaleX               = animators.scaleX.scalar.value,
            scaleY               = animators.scaleY.scalar.value,
            positionX            = animators.positionX,
            positionY            = animators.positionY,
            velocityX            = positionX.scalar.velocity,
            velocityY            = positionY.scalar.velocity,
            containScale         = this.calcContainScale(),
            autoCenterX          = this.autoCenterX,
            autoCenterY          = this.autoCenterY,
            reduceOverflowLeft   = this.reduceOverflowLeft,
            reduceOverflowRight  = this.reduceOverflowRight,
            reduceOverflowTop    = this.reduceOverflowTop,
            reduceOverflowBottom = this.reduceOverflowBottom;

        // Don't auto-align along x-axis if transitioning along X-axis.
        if (!positionX.isAttracted() || positionX.isDone()) {

            // Automatically center the page
            if (autoCenterX) {
                // Only center if the page is small enough to fit entirely
                // inside the viewport
                if (containScale && scaleX <= containScale && scaleY <= containScale) {
                    this._autoCenterX(autoCenterX);
                }
            }

            // Reduce overflow if not auto-centering
            else {
                if (reduceOverflowLeft && this.calcOverflowLeft() > 0 && velocityX <= 0) {
                    this._reduceOverflowLeft(reduceOverflowLeft);
                }

                if (reduceOverflowRight && this.calcOverflowRight() > 0 && velocityX >= 0) {
                    this._reduceOverflowRight(reduceOverflowRight);
                }
            }
        }

        // Vertical alignment
        if (!positionY.isAttracted() || positionY.isDone()) {

            // Automatically center the page
            if (autoCenterY) {
                // Only center if the page is small enough to fit entirely
                // inside the viewport
                if (containScale && scaleX <= containScale && scaleY <= containScale) {
                    this._autoCenterY(autoCenterY);
                }
            }

            // Reduce overflow if not auto-centering
            else {
                if (reduceOverflowTop && this.calcOverflowTop() > 0 && velocityY <= 0) {
                    this._reduceOverflowTop(reduceOverflowTop);
                }

                if (reduceOverflowBottom && this.calcOverflowBottom() > 0 && velocityY >= 0) {
                    this._reduceOverflowBottom(reduceOverflowBottom);
                }
            }
        }
    },

    /**
     * Help center the page horizontally within the viewport.
     *
     * Supported modes:
     *  - `"guide"` Only allows moving closer to centered position
     *  - `"snap"` Instantly snaps to a horizontally centered position
     *  - `"animate"` Sets a new animation target to horizontally centered
     *    position
     *  - `false` No automatic centering
     *
     * This method is not meant for external use and does *not* check if all
     * required parameters are defined.
     *
     * @param  {string}  mode  The alignment mode
     * @private
     */
    _autoCenterX: function(mode) {
        var delta    = this.calcDistanceFromHorizontalCenter(),
            animator = this.animators.positionX,
            scalar   = animator.scalar,
            velocity = scalar.velocity;

        if (mode === false || delta === 0) {
            return;
        }

        // Prevent the user from moving away from centered position
        if (mode === "guide") {
            if (delta < 0 && velocity < 0) {
                if (delta < velocity) {
                    scalar.value -= velocity;
                }
                else {
                    scalar.value -= delta;
                }
                animator.stop();
            }

            if (delta > 0 && velocity > 0) {
                if (delta > velocity) {
                    scalar.value -= velocity;
                }
                else {
                    scalar.value -= delta;
                }
                animator.stop();
            }
        }

        // Target centered position
        else if (mode === "snap" || mode === "animate") {
            this.set("targetPositionX", "center");
            mode === "snap" && animator.snap();
        }
    },

    /**
     * Help center the page vertically within the viewport.
     *
     * Supported modes:
     *  - `"guide"` Only allows moving closer to centered position
     *  - `"snap"` Instantly snaps to a vertically centered position
     *  - `"animate"` Sets a new animation target to vertically centered
     *    position
     *  - `false` No automatic centering
     *
     * This method is not meant for external use and does *not* check if all
     * required parameters are defined.
     *
     * @param  {string}  mode  The alignment mode
     * @private
     */
    _autoCenterY: function(mode) {
        var delta    = this.calcDistanceFromVerticalCenter(),
            animator = this.animators.positionY,
            scalar   = animator.scalar,
            velocity = scalar.velocity;

        if (mode === false || delta === 0) {
            return;
        }

        // Prevent the user from moving away from centered position
        if (mode === "guide") {
            if (delta < 0 && velocity < 0) {
                if (delta < velocity) {
                    scalar.value -= velocity;
                }
                else {
                    scalar.value -= delta;
                }
                animator.stop();
            }

            if (delta > 0 && velocity > 0) {
                if (delta > velocity) {
                    scalar.value -= velocity;
                }
                else {
                    scalar.value -= delta;
                }
                animator.stop();
            }
        }

        // Target centered position
        else if (mode === "snap" || mode === "animate") {
            this.set("targetPositionY", "center");
            mode === "snap" && animator.snap();
        }
    },

    /**
     * Help prevent the page from overflowing the viewport on the left any
     * further than is strictly necessary to reveal the right side of the page
     * inside of the viewport.
     *
     * Supported modes:
     *  - `"guide"` Helps prevent the page from overflowing the viewport on the
     *    left any further than is necessary to reveal the right side of the
     *    page inside of the viewport.
     *  - `"snap"` Prevents overflowing any further than is necessary to reveal
     *    the right side of the page inside of the viewport by instantly
     *    snapping to the position of least overflow.
     *  - `"animate"` Prevents overflowing any further than is necessary to
     *    reveal the right side of the page inside of the viewport by setting a
     *    new animation target to the position of least overflow.
     *  - `false` Allow overflow
     *
     * This method is not meant for external use and does *not* check if all
     * required parameters are defined.
     *
     * @param  {string}  mode  The alignment mode
     * @private
     */
    _reduceOverflowLeft: function(mode) {
        var overflow    = this.calcOverflowLeft(),
            width       = this.calcWidth(),
            viewport    = this.viewport,
            maxOverflow = width > viewport.width ? width - viewport.width : 0,
            animator    = this.animators.positionX,
            scalar      = animator.scalar,
            velocity    = scalar.velocity;

        // Nothing to reduce
        if (mode === false || overflow <= maxOverflow) {
            return;
        }

        // Prevent the user from increasing the amount of overflow.
        if (mode === "guide" && velocity < 0) {
            scalar.value += Math.min(-velocity, overflow - maxOverflow);
            animator.stop();
        }

        // Target non-overflowing positionX.
        else if (mode === "snap" || mode === "animate") {
            this.set("targetPositionX", width <= viewport.width ? "alignLeft" : "alignRight");
            mode === "snap" && animator.snap();
        }
    },

    /**
     * Help prevent the page from overflowing the viewport on the right.
     *
     * Supported modes:
     *  - `"guide"` Helps prevent the page from overflowing the viewport on the
     *    right any further than is necessary to reveal the left side of the
     *    page inside of the viewport.
     *  - `"snap"` Prevents overflowing any further than is necessary to reveal
     *    the left side of the page inside of the viewport by instantly
     *    snapping to the position of least overflow.
     *  - `"animate"` Prevents overflowing any further than is necessary to
     *    reveal the left side of the page inside of the viewport by setting a
     *    new animation target to the position of least overflow.
     *  - `false` Allow overflow
     *
     * This method is not meant for external use and does *not* check if all
     * required parameters are defined.
     *
     * @param  {string}  mode  The alignment mode
     * @private
     */
    _reduceOverflowRight: function(mode) {
        var overflow    = this.calcOverflowRight(),
            width       = this.calcWidth(),
            viewport    = this.viewport,
            maxOverflow = width > viewport.width ? width - viewport.width : 0,
            animator    = this.animators.positionX,
            scalar      = animator.scalar,
            velocity    = scalar.velocity;

        // Nothing to reduce
        if (mode === false || overflow <= maxOverflow) {
            return;
        }

        // Prevent the user from increasing the amount of overflow.
        if (mode === "guide" && velocity > 0) {
            scalar.value -= Math.min(velocity, overflow - maxOverflow);
            animator.stop();
        }

        // Target non-overflowing positionX.
        else if (mode === "snap" || mode === "animate") {
            this.set("targetPositionX", width <= viewport.width ? "alignRight" : "alignLeft");
            mode === "snap" && animator.snap();
        }
    },

    /**
     * Help prevent the page from overflowing the viewport on the top.
     *
     * Supported modes:
     *  - `"guide"` Helps prevent the page from overflowing the viewport on the
     *    top any further than is necessary to reveal the bottom of the
     *    page inside of the viewport.
     *  - `"snap"` Prevents overflowing any further than is necessary to reveal
     *    the bottom of the page inside of the viewport by instantly
     *    snapping to the position of least overflow.
     *  - `"animate"` Prevents overflowing any further than is necessary to
     *    reveal the bottom of the page inside of the viewport by setting a
     *    new animation target to the position of least overflow.
     *  - `false` Allow overflow
     *
     * This method is not meant for external use and does *not* check if all
     * required parameters are defined.
     *
     * @param  {string}  mode  The alignment mode
     * @private
     */
    _reduceOverflowTop: function(mode) {
        var overflow    = this.calcOverflowTop(),
            height      = this.calcHeight(),
            viewport    = this.viewport,
            maxOverflow = height > viewport.height ? height - viewport.height : 0,
            animator    = this.animators.positionY,
            scalar      = animator.scalar,
            velocity    = scalar.velocity;

        // Nothing to reduce
        if (mode === false || overflow <= maxOverflow) {
            return;
        }

        // Prevent the user from increasing the amount of overflow.
        if (mode === "guide" && velocity < 0) {
            scalar.value += Math.min(-velocity, overflow - maxOverflow);
            animator.stop();
        }

        // Target non-overflowing positionY.
        else if (mode === "snap" || mode === "animate") {
            this.set("targetPositionY", height <= viewport.height ? "alignTop" : "alignBottom");
            mode === "snap" && animator.snap();
        }
    },

    /**
     * Help prevent the page from overflowing the viewport on the bottom.
     *
     * Supported modes:
     *  - `"guide"` Helps prevent the page from overflowing the viewport on the
     *    bottom any further than is necessary to reveal the top of the
     *    page inside of the viewport.
     *  - `"snap"` Prevents overflowing any further than is necessary to reveal
     *    the top of the page inside of the viewport by instantly
     *    snapping to the position of least overflow.
     *  - `"animate"` Prevents overflowing any further than is necessary to
     *    reveal the top of the page inside of the viewport by setting a
     *    new animation target to the position of least overflow.
     *  - `false` Allow overflow
     *
     * This method is not meant for external use and does *not* check if all
     * required parameters are defined.
     *
     * @param  {string}  mode  The alignment mode
     * @private
     */
    _reduceOverflowBottom: function(mode) {
        var overflow    = this.calcOverflowBottom(),
            height      = this.calcHeight(),
            viewport    = this.viewport,
            maxOverflow = height > viewport.height ? height - viewport.height : 0,
            animator    = this.animators.positionY,
            scalar      = animator.scalar,
            velocity    = scalar.velocity;

        // Nothing to reduce
        if (mode === false || overflow <= maxOverflow) {
            return;
        }

        // Prevent the user from increasing the amount of overflow.
        if (mode === "guide" && velocity > 0) {
            scalar.value -= Math.min(velocity, overflow - maxOverflow);
            animator.stop();
        }

        // Target non-overflowing positionY.
        else if (mode === "snap" || mode === "animate") {
            this.set("targetPositionY", height <= viewport.height ? "alignBottom" : "alignTop");
            mode === "snap" && animator.snap();
        }
    },

    // ...........................
    // Resize Transform

    /**
     * Reflows the transform in response to changed viewport dimensions.
     *
     * A multiplier can be provided to resize the transform scalars and numeric
     * animation targets. Computed animation targets will be re-computed and
     * re-applied as well.
     *
     * @param  {number}  multiplier  The multiplier
     * @public
     */
    reflow: function(multiplier) {
        var animators           = this.animators,
            targetScaleX        = this.targetScaleX,
            targetScaleY        = this.targetScaleY,
            targetPositionX     = this.targetPositionX,
            targetPositionY     = this.targetPositionY,
            canComputeTransform = this.canComputeTransform(),
            scaleX              = animators.scaleX,
            scaleY              = animators.scaleY,
            positionX           = animators.positionX,
            positionY           = animators.positionY,
            snapScaleX,
            snapScaleY,
            snapPositionX,
            snapPositionY;

        if (canComputeTransform) {
            snapScaleX    = scaleX.isDone() && scaleX.isAttracted();
            snapScaleY    = scaleY.isDone() && scaleY.isAttracted();
            snapPositionX = positionX.isDone() && positionX.isAttracted();
            snapPositionY = positionY.isDone() && positionY.isAttracted();
        }

        // Resize and re-apply animation targets
        if (typeof targetScaleX === "number") {
            // This will automatically apply the new target
            this.set("targetScaleX", targetScaleX * multiplier);
        }
        else if (typeof targetScaleX === "string") {
            canComputeTransform && this._applyTargetScaleX();
        }

        if (typeof targetScaleY === "number") {
            // This will automatically apply the new target
            this.set("targetScaleY", targetScaleY * multiplier);
        }
        else if (typeof targetScaleY === "string") {
            canComputeTransform && this._applyTargetScaleY();
        }

        if (typeof targetPositionX === "number") {
            // This will automatically apply the new target
            this.set("targetPositionX", targetPositionX * multiplier);
        }
        else if (typeof targetPositionX === "string") {
            canComputeTransform && this._applyTargetPositionX();
        }

        if (typeof targetPositionY === "number") {
            // This will automatically apply the new target
            this.set("targetPositionY", targetPositionY * multiplier);
        }
        else if (typeof targetPositionY === "string") {
            canComputeTransform && this._applyTargetPositionY();
        }

        // If the aspect ratio of the viewport changed, a computed animation
        // target may change in ways not covered by the supplied multiplier. In
        // order to keep previously finished animations finished, we need to
        // snap the animated property back into the newly computed animation
        // target. For unfinished transitions, we simply resize the scalar.
        if (canComputeTransform) {
            snapScaleX ? scaleX.finish() : scaleX.scalar.resize(multiplier);
            snapScaleY ? scaleY.finish() : scaleY.scalar.resize(multiplier);

            // Enforce min/max scale
            scaleX.scalar.value = this.constrainScale(scaleX.scalar.value);
            scaleY.scalar.value = this.constrainScale(scaleY.scalar.value);

            snapPositionX ? positionX.finish() : positionX.scalar.resize(multiplier);
            snapPositionY ? positionY.finish() : positionY.scalar.resize(multiplier);
        }

        // Animation targets have updated, now lets resize the scalars.
        else {
            scaleX.scalar.resize(multiplier);
            scaleY.scalar.resize(multiplier);

            // Enforce min/max scale
            scaleX.scalar.value = this.constrainScale(scaleX.scalar.value);
            scaleY.scalar.value = this.constrainScale(scaleY.scalar.value);

            positionX.scalar.resize(multiplier);
            positionY.scalar.resize(multiplier);
        }

    },

    // ...........................
    // Update Transform

    /**
     * Remove all animation targets.
     *
     * @public
     */
    clearAttractors: function() {
        this.set("targetScaleX", null);
        this.set("targetScaleY", null);
        this.set("targetPositionX", null);
        this.set("targetPositionY", null);
    },

    /**
     * Update the simulation and enqueue the next frame as needed.
     *
     * @public
     */
    update: kind.inherit(function(sup) {
        return function() {
            var animators = this.animators,
                scaleX    = animators.scaleX,
                scaleY    = animators.scaleY,
                positionX = animators.positionX,
                positionY = animators.positionY;

            if (!this.canComputeTransform()) {
                return;
            }

            // Update animation
            sup.apply(this, arguments);

            // Snap precisely to target value when animation is done
            scaleX.isDone() && scaleX.finish();
            scaleY.isDone() && scaleY.finish();
            positionX.isDone() && positionX.finish();
            positionY.isDone() && positionY.finish();

            // Enforce min/max scale
            scaleX.scalar.value = this.constrainScale(scaleX.scalar.value);
            scaleY.scalar.value = this.constrainScale(scaleY.scalar.value);

            // Apply automatic alignment settings.
            this.autoAlignment();
        };
    }),

    /**
     * Snaps the current animation to it's final state, then automatically snaps
     * any automatic alignment.
     *
     * @public
     */
    finish: kind.inherit(function(sup) {
        return function() {
            if (!this.canComputeTransform()) {
                return;
            }

            // Finish animation
            sup.apply(this, arguments);

            // Apply any automatic alignment that might be active.
            this.autoAlignment();

            // Finish any new targets that might have been set by auto-alignment
            if (!this.isDone()) {
                sup.apply(this, arguments);
            }
        };
    })

});
